// Contains base styles for the FullCalendar component, using CSS variables that integrate with Chakra UI theme.

export const baseCalendarStyles = `
  /* General Container Styles */
  .calendar-container {
    /* Light Mode Colors */
    --fc-border-color: var(--chakra-colors-gray-100);
    --fc-today-bg-color: var(--chakra-colors-pink-50);
    --fc-today-border-color: var(--chakra-colors-pink-300);
    --fc-event-bg-color: var(--chakra-colors-pink-500); /* Default event bg */
    --fc-event-border-color: var(--chakra-colors-pink-500); /* Default event border */
    --fc-event-text-color: white; /* Default event text */
    --fc-page-bg-color: white;
    --fc-neutral-bg-color: var(--chakra-colors-gray-50);
    --fc-list-event-hover-bg-color: white;
    --fc-highlight-color: var(--chakra-colors-blue-50);
    --fc-selected-border-color: var(--chakra-colors-blue-400);
    color: var(--chakra-colors-gray-700); /* Default text color */
    font-size: 0.875rem; /* Base font size */
  }

  /* Dark Mode Overrides */
  .dark .calendar-container {
    --fc-border-color: var(--chakra-colors-gray-700);
    --fc-today-bg-color: var(--chakra-colors-pink-900); /* Slightly pinkish tint for today */
    --fc-today-border-color: var(--chakra-colors-pink-500);
    --fc-page-bg-color: var(--chakra-colors-gray-800);
    --fc-neutral-bg-color: var(--chakra-colors-gray-900);
    --fc-list-event-hover-bg-color: var(--chakra-colors-gray-700);
    --fc-highlight-color: var(--chakra-colors-blue-900);
    --fc-selected-border-color: var(--chakra-colors-blue-500);
    color: var(--chakra-colors-gray-200);
  }

  /* Basic Structure & Borders */
  .calendar-container .fc-theme-standard td,
  .calendar-container .fc-theme-standard th {
    border-color: var(--fc-border-color);
    border-width: 0.5px;
  }

  .calendar-container .fc-scrollgrid {
    border: none !important; /* Remove default FullCalendar border */
  }

  /* 减少边框显示 */
  .calendar-container .fc-theme-standard .fc-scrollgrid {
    border-collapse: separate;
    border-spacing: 1px;
  }

  /* Toolbar */
  .calendar-container .fc-toolbar-title {
    font-size: 1.5rem !important;
    font-weight: 500;
    color: inherit; /* Inherit from container */
  }

  .calendar-container .fc-toolbar.fc-header-toolbar {
    margin-bottom: 1.5em; /* Add space below toolbar */
    padding: 0 0.5rem; /* Add some padding */
  }

  /* Toolbar Buttons (Prev, Next, Today) */
  .calendar-container .fc-button {
    background: transparent !important;
    border: 1px solid var(--chakra-colors-gray-300) !important;
    box-shadow: var(--chakra-shadows-sm) !important;
    color: var(--chakra-colors-gray-700) !important;
    padding: 0.375rem 0.75rem !important;
    font-size: 0.875rem !important;
    border-radius: var(--chakra-radii-md) !important;
    transition: background-color 0.2s ease;
  }
  .calendar-container .fc-button:hover {
    background: var(--chakra-colors-gray-100) !important;
  }
  .calendar-container .fc-button .fc-icon {
    font-size: 1.1em; /* Adjust icon size */
  }
  .dark .calendar-container .fc-button {
     border-color: var(--chakra-colors-gray-600) !important;
     color: var(--chakra-colors-gray-200) !important;
  }
  .dark .calendar-container .fc-button:hover {
    background: var(--chakra-colors-gray-700) !important;
  }
  /* Today button specific styling */
  .calendar-container .fc-today-button {
    /* Override default button style for Today button */
    background: var(--chakra-colors-blue-100) !important;
    border-color: var(--chakra-colors-blue-200) !important;
    color: var(--chakra-colors-blue-800) !important;
  }
  .calendar-container .fc-today-button:hover {
    background: var(--chakra-colors-blue-200) !important;
  }
  .dark .calendar-container .fc-today-button {
    background: var(--chakra-colors-blue-800) !important;
    border-color: var(--chakra-colors-blue-700) !important;
    color: var(--chakra-colors-blue-100) !important;
  }
  .dark .calendar-container .fc-today-button:hover {
    background: var(--chakra-colors-blue-700) !important;
  }
  .calendar-container .fc-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Day Grid View */
  .calendar-container .fc-daygrid-day {
    transition: all 0.2s ease;
    padding: 1px;
  }

  .calendar-container .fc-daygrid-day-frame {
    border-radius: 6px;
    overflow: hidden;
    height: 100%;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .calendar-container .fc-daygrid-day:hover .fc-daygrid-day-frame {
    box-shadow: 0 0 0 1px var(--chakra-colors-gray-200);
  }

  .dark .calendar-container .fc-daygrid-day:hover .fc-daygrid-day-frame {
    box-shadow: 0 0 0 1px var(--chakra-colors-gray-600);
  }

  .calendar-container .fc-daygrid-day-number {
    font-size: 0.875rem;
    color: inherit; /* Inherit from container */
    padding: 0.5rem;
    font-weight: 500;
  }

  .calendar-container .fc-day-sat,
  .calendar-container .fc-day-sun {
    background-color: rgba(0,0,0,0.02);
  }
  .dark .calendar-container .fc-day-sat,
  .dark .calendar-container .fc-day-sun {
    background-color: rgba(255,255,255,0.02);
  }

  .calendar-container .fc-daygrid-day.fc-day-today {
    background: transparent !important;
    position: relative;
    z-index: 1;
  }

  .calendar-container .fc-daygrid-day.fc-day-today .fc-daygrid-day-frame {
    box-shadow: none;
  }

  .calendar-container .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
    font-weight: 700; /* Make today's number bold */
    position: relative;
    background-color: var(--fc-today-border-color);
    color: white;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .calendar-container .fc-daygrid-day:hover {
    background: var(--chakra-colors-gray-50);
  }
  .dark .calendar-container .fc-daygrid-day:hover {
    background: var(--chakra-colors-gray-700);
  }
  .calendar-container .fc-day-other .fc-daygrid-day-number {
    color: var(--chakra-colors-gray-400);
  }
  .dark .calendar-container .fc-day-other .fc-daygrid-day-number {
    color: var(--chakra-colors-gray-500);
  }

  /* Custom Highlight for Selected Day */
  .calendar-container .selected-day-highlight {
    background-color: var(--fc-highlight-color) !important;
    position: relative;
    z-index: 0;
  }

  .calendar-container .selected-day-highlight .fc-daygrid-day-frame {
    box-shadow: inset 0 0 0 2px var(--fc-selected-border-color), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  .calendar-container .selected-day-highlight .fc-daygrid-day-number {
    font-weight: 600; /* Make selected day number semi-bold */
  }

  /* Optional: Slightly adjust today's highlight if it overlaps with selected */
  .calendar-container .fc-day-today.selected-day-highlight {
    z-index: 2; /* Ensure today + selected has highest z-index */
  }

  /* Combine today and selected styles with improved visual cues */
  .calendar-container .fc-day-today.selected-day-highlight .fc-daygrid-day-frame {
    background-color: var(--fc-today-bg-color) !important;
    box-shadow:
      inset 0 0 0 1px var(--fc-today-border-color),
      inset 0 0 0 3px var(--fc-selected-border-color),
      0 4px 8px -2px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  /* Enhanced time grid styling to distinguish today and selected */
  .calendar-container .fc-timegrid-col.fc-day-today {
    background: var(--fc-today-bg-color) !important;
    position: relative;
  }

  .calendar-container .fc-timegrid-col.fc-day-today:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--fc-today-border-color);
    z-index: 3;
  }

  .calendar-container .fc-timegrid-col.selected-day-highlight {
    background: var(--fc-highlight-color) !important;
    box-shadow: inset 0 0 0 1px var(--fc-selected-border-color);
    position: relative;
  }

  .calendar-container .fc-timegrid-col.fc-day-today.selected-day-highlight:before {
    background: var(--fc-selected-border-color);
  }

  /* Current Time Indicator (nowIndicator) */
  .calendar-container .fc-timegrid-now-indicator-line {
    border-color: var(--chakra-colors-red-500) !important;
    border-width: 1.5px !important;
    z-index: 4 !important;
    opacity: 0.8;
    box-shadow: 0 0 8px rgba(229, 62, 62, 0.6); /* Red glow effect */
  }

  .calendar-container .fc-timegrid-now-indicator-arrow {
    border-color: var(--chakra-colors-red-500) !important;
    border-radius: 50% !important;
    border-width: 5px !important;
    margin-top: -5px !important;
    right: 0 !important;
    left: auto !important;
  }

  /* Dark mode adjustments */
  .dark .calendar-container .fc-timegrid-now-indicator-line {
    border-color: var(--chakra-colors-red-400) !important;
    box-shadow: 0 0 8px rgba(254, 178, 178, 0.4); /* Lighter red glow in dark mode */
  }

  .dark .calendar-container .fc-timegrid-now-indicator-arrow {
    border-color: var(--chakra-colors-red-400) !important;
  }

  /* Time Grid View */
  .calendar-container .fc-timegrid-slot-label {
    color: var(--chakra-colors-gray-500);
  }
  .dark .calendar-container .fc-timegrid-slot-label {
    color: var(--chakra-colors-gray-400);
  }
  .calendar-container .fc-timegrid-slot {
    height: 2.5em; /* Adjust slot height */
  }
  .calendar-container .fc-timegrid-col.fc-day-today {
     background: var(--fc-today-bg-color);
  }

  /* General Event Styling */
  .calendar-container .fc-event {
    border-radius: 4px;
    padding: 2px 5px;
    font-size: 0.75rem;
    border: none;
    cursor: pointer;
    transition: filter 0.2s ease, box-shadow 0.2s ease;
    color: var(--fc-event-text-color); /* Use custom prop */
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .calendar-container .fc-event:hover {
    filter: brightness(0.9);
    box-shadow: var(--chakra-shadows-md);
  }
  .calendar-container .fc-event .fc-event-main {
    color: inherit; /* Inherit text color */
  }
  /* Style for the time shown inside timed events */
  .calendar-container .fc-event-time {
    font-weight: 500;
    margin-right: 4px;
    display: none; /* Hide default time, we render it inside custom content if needed */
  }
  /* Style for the title shown inside events */
  .calendar-container .fc-event-title {
    /* Default title is hidden by eventContent, style the custom container */
  }

  /* Styles for custom event content */
  .calendar-container .fc-event .custom-event-content {
    /* Ensure flex layout works correctly */
  }

  .calendar-container .fc-event .event-title-text {
    /* Ensure text wraps and doesn't overflow badly */
    line-height: 1.3; /* Adjust line height for wrapped text */
    /* text-overflow: ellipsis; - Might be redundant with word-break */
  }

  /* All-day event styling */
  .calendar-container .fc-daygrid-event {
     margin-top: 2px;
     margin-bottom: 2px;
  }

  /* More Link (+n more) */
  .calendar-container .fc-daygrid-more-link {
    /* Styling applied via moreLinkContent in component */
    float: right;
    margin-top: 1px;
  }

  /* Task Status Styling (Applied via classNames in taskToEvents) */
  .calendar-container .task-done {
    /* opacity: 0.7; --- Opacity is now applied directly in renderEventContent */
  }
  .calendar-container .task-undone {
    /* opacity: 1; */
  }

  /* 移除所有边框样式，由组件内联样式控制 */
  .calendar-container .fc-event {
    border: none !important;
    background-color: transparent !important;
  }

  /* Column Headers (Month/Week/Day names) */
  .calendar-container .fc-col-header-cell {
    padding: 0.75rem 0;
    font-weight: 400;
    font-size: 0.875rem;
    color: var(--chakra-colors-gray-600);
    background: var(--fc-page-bg-color); /* Match page background */
    border-bottom-width: 0;
  }
  .dark .calendar-container .fc-col-header-cell {
    color: var(--chakra-colors-gray-400);
  }

  /* List View Specific Styles */
  .calendar-container .fc-list {
    border: none !important;
  }

  /* List View - Day Header */
  .calendar-container .fc-list-day {
    /* No extra styles needed currently */
  }
  .calendar-container .fc-list-day-cushion {
    background: var(--fc-neutral-bg-color) !important;
    padding: 12px 16px !important;
    margin: 8px 0 !important;
  }
  .calendar-container .fc-list-day-text,
  .calendar-container .fc-list-day-side-text {
    font-size: 0.9rem !important;
    font-weight: 600 !important;
    color: inherit !important; /* Inherit from container */
  }
  .calendar-container .fc-list-day-side-text {
    font-weight: normal !important;
    opacity: 0.8;
  }

  /* List View - Event Row */
  .calendar-container .fc-list-event {
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px !important;
    margin: 4px 8px !important;
    border: none !important;
    background: var(--fc-page-bg-color) !important; /* Use page background */
    box-shadow: var(--chakra-shadows-sm);
  }
  .dark .calendar-container .fc-list-event {
    box-shadow: var(--chakra-shadows-dark-sm);
  }
  .calendar-container .fc-list-event:hover {
    transform: translateY(-1px);
    box-shadow: var(--chakra-shadows-md);
    background: var(--fc-list-event-hover-bg-color) !important;
  }
  .dark .calendar-container .fc-list-event:hover {
     box-shadow: var(--chakra-shadows-dark-md);
  }
  .calendar-container .fc-list-event-dot {
    /* Hide default dot, using event color for time */
    display: none !important;
  }

  /* List View - Event Time */
  .calendar-container .fc-list-event-time {
    font-size: 0.875rem !important;
    color: var(--fc-event-border-color) !important; /* Use event border color for time */
    width: 110px !important;
    padding: 10px 12px !important;
    font-feature-settings: "tnum"; /* Tabular nums for alignment */
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }
  /* List View - Event Title */
  .calendar-container .fc-list-event-title {
    font-size: 0.875rem !important;
    padding: 10px 12px !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    color: inherit; /* Inherit color from container */
  }
  .calendar-container .fc-list-event-title:before {
     /* Remove the default line/dot */
     content: none;
  }

  /* List View - Empty State */
  .calendar-container .fc-list-empty {
    background: transparent !important;
    padding: 48px 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 12px !important;
    color: var(--chakra-colors-gray-400) !important;
  }
  .calendar-container .fc-list-empty:before {
    content: "📅";
    font-size: 2rem;
    margin-bottom: 8px;
  }

  /* Scrollbar Styling (Optional - Webkit/Firefox) */
  .calendar-container .fc-scroller {
    scrollbar-width: thin; /* Firefox */
    scrollbar-color: var(--chakra-colors-gray-300) transparent; /* Firefox */
  }
  .calendar-container .fc-scroller::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  .calendar-container .fc-scroller::-webkit-scrollbar-track {
    background: transparent;
  }
  .calendar-container .fc-scroller::-webkit-scrollbar-thumb {
    background-color: var(--chakra-colors-gray-300);
    border-radius: 4px;
    border: 2px solid transparent; /* Padding around thumb */
    background-clip: padding-box;
  }
  .calendar-container .fc-scroller::-webkit-scrollbar-thumb:hover {
    background-color: var(--chakra-colors-gray-400);
  }
  .dark .calendar-container .fc-scroller {
     scrollbar-color: var(--chakra-colors-gray-600) transparent;
  }
  .dark .calendar-container .fc-scroller::-webkit-scrollbar-thumb {
    background-color: var(--chakra-colors-gray-600);
  }
  .dark .calendar-container .fc-scroller::-webkit-scrollbar-thumb:hover {
    background-color: var(--chakra-colors-gray-500);
  }

  /* === Enhanced Styles for Additional Timezone Axes === */

  /* Base style for all additional axis cells (th and td) */
  .calendar-container .additional-tz-axis {
    width: 55px !important; /* Slightly narrower width */
    font-size: 0.7em !important; /* Smaller font */
    text-align: center;
    vertical-align: top; /* Align content to the top */
    border-left: 1px solid var(--chakra-colors-gray-100);
    background-color: rgba(128, 128, 128, 0.02); /* Subtle background */
    padding: 0.1rem 0.1rem;
  }

  .dark .calendar-container .additional-tz-axis {
    border-left-color: var(--chakra-colors-gray-700);
    background-color: rgba(255, 255, 255, 0.015);
  }

  /* Header specific styles */
  .calendar-container .fc-col-header th.additional-tz-axis {
    font-weight: 500; /* Slightly bolder than body */
    white-space: normal; /* Allow wrapping for long names */
    padding: 0.4rem 0.1rem; /* Adjust padding */
    color: var(--chakra-colors-gray-600);
    border-bottom: 1px solid var(--fc-border-color); /* Match main header border */
  }

  .dark .calendar-container .fc-col-header th.additional-tz-axis {
    color: var(--chakra-colors-gray-400);
  }

  /* Body time slot specific styles */
  .calendar-container .fc-timegrid-slots td.additional-tz-axis {
    padding-top: 0.25rem; /* Align text slightly below the top line */
    color: var(--chakra-colors-gray-500);
    border-bottom: 1px dotted var(--chakra-colors-gray-100); /* Match slot lines */
    border-left-style: dashed; /* Make left border dashed */
  }

  .dark .calendar-container .fc-timegrid-slots td.additional-tz-axis {
    color: var(--chakra-colors-gray-400);
    border-bottom-color: var(--chakra-colors-gray-700);
    border-left-color: var(--chakra-colors-gray-600);
  }

  /* Minor slot rows (half-hour marks) - less prominent */
  .calendar-container .fc-timegrid-slots tr:has(td.fc-timegrid-slot-minor) td.additional-tz-axis {
     border-bottom-style: dashed;
  }

  /* Inner cushion element within the body cell */
  .calendar-container .fc-timegrid-slots td.additional-tz-axis .fc-timegrid-slot-label-cushion {
    padding: 0 !important; /* Remove default cushion padding */
    display: block; /* Ensure it takes up space */
    text-align: center;
  }
`;